import {
  createTheme,
  rem,
  Button,
  Card,
  Input,
  InputWrapper,
  Text,
  SegmentedControl,
  Modal,
  Skeleton,
  Table,
  Checkbox,
} from '@mantine/core';
import { projectConfigs } from './projectConfigs';

const colorThemeVariable = import.meta.env.VITE_COLOR_THEME;
const { colorTheme } = (projectConfigs[colorThemeVariable] ||
  projectConfigs.default)!;

export const theme = createTheme({
  cursorType: 'pointer',
  fontFamily:
    '"Instrument Sans", -apple-system, BlinkMacSystemFont, Segoe UI, Inter, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif',
  colors: colorTheme.colors,
  defaultGradient: {
    deg: 225,
    from: 'rgb(from var(--mantine-color-brand-1) r g b / var(--button-bg-opacity, 1))',
    to: 'rgb(from var(--mantine-color-brand-0) r g b / var(--button-bg-opacity, 1))',
  },
  primaryColor: 'brand',
  primaryShade: 0,
  black: colorTheme.colors!.dark?.[9] || '#0a0b0d',
  white: colorTheme.colors!.dark?.[0] || '#F1F1F1',
  fontSizes: {
    xs: rem(14),
    sm: rem(16),
    md: rem(20),
    lg: rem(24),
    xl: rem(32),
  },
  components: {
    Text: Text.extend({
      defaultProps: { size: 'xs', c: 'var(--mantine-color-main-0)', fw: 500 },
    }),
    Skeleton: Skeleton.extend({
      defaultProps: {
        // @ts-ignore-next-line
        component: 'span',
        display: 'block',
      },
    }),
    Button: Button.extend({
      defaultProps: { size: 'sm' },
      vars: (_, props) => {
        const colorVars =
          props.variant === 'gradient' ||
          props.variant === 'primary' ||
          !props.variant
            ? {
                '--button-color': colorTheme.primaryButtonTextColor,
              }
            : undefined;
        if (props.size === 'sm') {
          return {
            root: {
              '--button-height': rem(40),
              '--button-padding-x': rem(16),
              '--button-radius': rem(16),
              '--button-fz': rem(16),
              '--button-fw': 600,
              ...colorVars,
            },
          };
        }

        if (props.size === 'lg') {
          return {
            root: {
              '--button-height': rem(56),
              '--button-padding-x': rem(16),
              '--button-radius': rem(16),
              '--button-fz': rem(16),
              '--button-fw': 700,
              ...colorVars,
            },
          };
        }

        return { root: {} };
      },
      classNames: {
        // label: 'font-[700]',
        root: 'hover:[--button-bg-opacity:0.7] disabled:[background:var(--button-bg)] disabled:text-main0 disabled:[--button-bg-opacity:0.25]',
      },
    }),
    Card: Card.extend({
      classNames: {
        root: 'rounded-[32px] border border-solid border-[#202027] shadow-[0_4px_4px_0_rgba(0,0,0,0.25)]',
      },
    }),
    Input: Input.extend({
      vars: (_, props) => {
        if (props.size === 'sm') {
          return {
            wrapper: {
              '--input-height': rem(30),
            },
          };
        }

        if (props.size === 'md') {
          return {
            wrapper: {
              '--input-height': rem(60),
            },
          };
        }

        return { wrapper: {} };
      },
      classNames: {
        input:
          'px-4 rounded-[16px] bg-[#101010]/[0.5] placeholder:text-[#87868C] placeholder:font-[500] text-white font-[500]',
      },
    }),
    InputWrapper: InputWrapper.extend({
      classNames: {
        root: 'leading-[1]',
        label: 'mb-2 font-[500] text-xs text-main0',
      },
    }),
    SegmentedControl: SegmentedControl.extend({
      classNames: {
        root: 'h-10 p-0 rounded-[12px] outline outline-primarybrand outline-1',
        indicator: 'bg-primarybrand rounded-none',
        label:
          'h-full py-0 px-6 leading-[40px] data-[active]:font-[700] capitalize',
      },
    }),
    Modal: Modal.extend({
      defaultProps: {
        size: 720,
        removeScrollProps: { removeScrollBar: false },
      },
      classNames: {
        content:
          'py-6 px-6 border border-solid border-[#202027] rounded-[32px] bg-[#0F1012] shadow-[0_4px_4px_0_rgba(0,0,0,0.25)]',
        header:
          'p-0 min-h-0 pb-6 mb-6 border-solid border-0 border-b border-b-white/[0.14]',
        body: 'p-0',
      },
    }),
    Table: Table.extend({
      classNames: {
        td: 'pr-4 has-[>a]:pr-0 [&>a]:pr-4',
        th: 'pr-4',
      },
    }),
    Checkbox: Checkbox.extend({
      classNames: {
        inner:
          'flex justify-center items-center w-5 h-5 rounded-[6px] border-[2px] border-solid border-[#272727] [&:has(input:checked)]:border-primarybrand',
        input:
          'w-3 h-3 bg-transparent border-transparent checked:bg-primarybrand rounded-[3px]',
        icon: 'hidden',
      },
    }),
  },
});
